import { useState, useEffect, useMemo, useCallback } from "react";
import { createContainer } from "unstated-next";
import { matchSorter } from "match-sorter";
import { sortBy as _sortBy, uniqBy as _uniqBy } from "lodash-es";
import { useQuery, useQueryClient } from "react-query";

import { Auth } from "shared/@auth/Auth";
import { useLogger } from "shared/@hooks/useLogger";

import { SORT_METHOD } from "./enums";

import { clone } from "lodash-es";

function useCMSData() {
  const queryClient = useQueryClient();
  const { makeAuthedRequest } = Auth.useContainer();
  const { logError } = useLogger({ prefix: "useCMSData" });

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilters, setSelectedFilters] = useState([
    {
      filterId: "status1",
      title: "Status",
      id: 1,
      label: "Published",
      filter: {
        type: "status",
        title: "Status",
        array: [
          {
            id: 1,
            label: "Published",
          },
          {
            id: 2,
            label: "In Review",
          },
          {
            id: 3,
            label: "Archived",
          },
        ],
      },
      type: "status",
    },
  ]); //default to Published. Why this data is required? No idea but it's what selecting Published produced.
  const [sortBy, setSortBy] = useState(SORT_METHOD.dateDesc);

  const { isLoading, isError, data, error, refetch } = useQuery(
    ["cms", "all"],
    () =>
      makeAuthedRequest("/cms/all").then(async (res) => {
        if (res.ok) {
          return res.json();
        } else {
          const body = await res.json();
          throw new Error(body.message);
        }
      })
  );

  // Log to screen when an API error occurs.
  useEffect(() => {
    if (isError) {
      logError(error);
    }
  }, [isError, error]);

  // Destructure data object from API. Return empty defaults in case data is null.
  const { items, settings, filters } = useMemo(() => {
    if (data == null) {
      return {
        items: [],
        settings: null,
        filters: [],
      };
    } else {
      return {
        items: data.content,
        settings: data.settings,
        filters: data.filters,
      };
    }
  }, [data]);

  const filteredItems = useMemo(() => {
    let results = clone(items);

    // get status id's as an independent sort condition
    let setStatuses = selectedFilters.filter(
      (filter) => filter.type === "status"
    );
    let statusIds = setStatuses.map((status) => status.id);

    const nonStatusFilters = selectedFilters.filter(
      (filter) => filter.type !== "status"
    );

    // Group non-status filters by type so AND is applied across types, OR within same type
    const filtersByType = nonStatusFilters.reduce((acc, filter) => {
      if (!acc[filter.type]) acc[filter.type] = [];
      acc[filter.type].push(filter);
      return acc;
    }, {});

    const matchFilter = (item, filter) => {
      if (
        filter.type === "membershipPermission" ||
        filter.type === "pinLevelPermission"
      ) {
        return (Number(item[filter.type]) & Number(filter.id)) !== 0;
      } else if (filter.type === "tags.hidden") {
        return item.tags.some(
          (tag) => tag.label === filter.label && tag.hidden
        );
      } else if (filter.type === "tags.internal") {
        return item.tags.some(
          (tag) => tag.label === filter.label && tag.internal
        );
      } else if (Array.isArray(item[filter.type])) {
        const selectedType = item[filter.type].map((x) =>
          x && x.id ? x.id : x
        );
        return selectedType.some(
          (value) => String(value) === String(filter.id)
        );
      } else {
        return item[filter.type] === Number(filter.id);
      }
    };

    results = results.filter((item) => {
      if (statusIds.length && !statusIds.includes(item.status)) return false;
      if (nonStatusFilters.length === 0) return true;
      // Item must satisfy at least one filter from every type group (AND across types, OR within type)
      return Object.values(filtersByType).every((filtersOfType) =>
        filtersOfType.some((filter) => matchFilter(item, filter))
      );
    });

    // Search within the selected filters using contiguous matches in searchable fields.
    const normalizedSearchTerm = searchTerm.trim();
    if (normalizedSearchTerm) {
      results = matchSorter(results, normalizedSearchTerm, {
        keys: ["title", "subTitle", "description", "tags.*.label"],
        threshold: matchSorter.rankings.CONTAINS,
      });
    }

    // Keep the selected ordering after narrowing the results by search.
    switch (sortBy) {
      case SORT_METHOD.titleAsc:
        results = _sortBy(results, (x) => x.title.toLowerCase());
        break;
      case SORT_METHOD.titleDesc:
        results = _sortBy(results, (x) => x.title.toLowerCase()).reverse();
        break;
      case SORT_METHOD.dateAsc:
        results = _sortBy(results, (x) => x.publishOn);
        break;
      case SORT_METHOD.dateDesc:
        results = _sortBy(results, (x) => x.publishOn).reverse();
        break;
      default:
        break;
    }

    return results;
  }, [items, selectedFilters, searchTerm, sortBy]);

  const archiveContent = useCallback(
    async (contentId, shouldArchive) => {
      const queryKey = ["cms", "all"];
      const contentQueryKey = ["cms", "content", contentId];
      await Promise.all([
        queryClient.cancelQueries(queryKey),
        queryClient.cancelQueries(contentQueryKey),
      ]);
      const previousData = queryClient.getQueryData(queryKey);
      const previousContent = queryClient.getQueryData(contentQueryKey);
      const nextStatus = shouldArchive ? 3 : 1;

      queryClient.setQueryData(queryKey, (currentData) => {
        if (!currentData || !Array.isArray(currentData.content)) {
          return currentData;
        }

        return {
          ...currentData,
          content: currentData.content.map((item) =>
            item.id === contentId ? { ...item, status: nextStatus } : item
          ),
        };
      });
      queryClient.setQueryData(contentQueryKey, (currentContent) =>
        currentContent
          ? { ...currentContent, status: nextStatus }
          : currentContent
      );

      try {
        const res = await makeAuthedRequest(`/cms/content/${contentId}`, {
          method: shouldArchive ? "DELETE" : "PUT",
          ...(shouldArchive ? {} : { body: JSON.stringify({ status: 1 }) }),
        });

        if (!res.ok) {
          const json = await res.json();
          throw new Error(json.message);
        }

        return res.json();
      } catch (error) {
        if (previousData !== undefined) {
          queryClient.setQueryData(queryKey, previousData);
        }
        if (previousContent !== undefined) {
          queryClient.setQueryData(contentQueryKey, previousContent);
        }
        logError(error);
      } finally {
        queryClient.invalidateQueries(queryKey);
        queryClient.invalidateQueries(contentQueryKey);
      }
    },
    [logError, makeAuthedRequest, queryClient]
  );

  return {
    // Request state
    isLoading,
    isError,
    error,

    // Request data
    items,
    settings,
    filters,

    // Processed data
    filteredItems,

    // State accessors
    selectedFilters,
    setSelectedFilters,
    sortBy,
    setSortBy,
    searchTerm,
    setSearchTerm,

    refreshAll: refetch,

    archiveContent,
  };
}

export const CMSData = createContainer(useCMSData);
